import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Setup from './pages/Setup';
import ContractsList from './pages/ContractsList';
import ContractEditor from './pages/ContractEditor';
import ContractView from './pages/ContractView';
import TemplatesList from './pages/TemplatesList';
import TemplateEditor from './pages/TemplateEditor';
import AdminBlocks from './pages/AdminBlocks';
import AdminSections from './pages/AdminSections';
import AdminSettings from './pages/AdminSettings';
import UserManagement from './pages/UserManagement';
import BlocksImport from './pages/BlocksImport';
import BlocksImportGuide from './pages/BlocksImportGuide';
import SignedDocuments from './pages/SignedDocuments';

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false }: { 
  children: React.ReactNode; 
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}) {
  const { isAuthenticated, isAdmin, isSuperAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/contracts" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/contracts" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route Component (redirects if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/contracts" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Setup Route - nur für initiale Einrichtung */}
      <Route path="/setup" element={<Setup />} />

      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path="/" element={<Navigate to="/contracts" replace />} />
      
      <Route path="/contracts" element={
        <ProtectedRoute>
          <ContractsList />
        </ProtectedRoute>
      } />
      
      <Route path="/contracts/:id" element={
        <ProtectedRoute>
          <ContractView />
        </ProtectedRoute>
      } />
      
      <Route path="/contracts/:id/edit" element={
        <ProtectedRoute>
          <ContractEditor />
        </ProtectedRoute>
      } />
      
      <Route path="/templates" element={
        <ProtectedRoute>
          <TemplatesList />
        </ProtectedRoute>
      } />

      <Route path="/templates/:id" element={
        <ProtectedRoute>
          <TemplateEditor />
        </ProtectedRoute>
      } />

      <Route path="/admin/templates/new" element={
        <ProtectedRoute adminOnly>
          <TemplateEditor />
        </ProtectedRoute>
      } />

      <Route path="/admin/templates/:id/edit" element={
        <ProtectedRoute adminOnly>
          <TemplateEditor />
        </ProtectedRoute>
      } />

      {/* Signed Documents */}
      <Route path="/signed-documents" element={
        <ProtectedRoute>
          <SignedDocuments />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/blocks" element={
        <ProtectedRoute adminOnly>
          <AdminBlocks />
        </ProtectedRoute>
      } />

      <Route path="/admin/blocks/import" element={
        <ProtectedRoute adminOnly>
          <BlocksImport />
        </ProtectedRoute>
      } />

      <Route path="/admin/blocks/import/anleitung" element={
        <ProtectedRoute adminOnly>
          <BlocksImportGuide />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/sections" element={
        <ProtectedRoute adminOnly>
          <AdminSections />
        </ProtectedRoute>
      } />

      <Route path="/admin/settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettings />
        </ProtectedRoute>
      } />

      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <UserManagement />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/contracts" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
